<template>
  <div class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-5 border-b border-gray-200 bg-gray-50">
        <h1 class="text-2xl font-bold text-gray-900 tracking-wide">Historial de Análisis Clínicos</h1>
        <p class="text-gray-600 mt-2 font-light">Repositorio de informes diagnósticos procesados mediante inteligencia artificial médica.</p>
      </div>
    </div>

    <!-- Upload Button -->
    <div class="bg-white shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-lg font-bold text-gray-900">Nuevo Análisis Clínico</h3>
            <p class="text-gray-600 mt-1 font-light">Cargue documentos de laboratorio para procesamiento diagnóstico inmediato</p>
          </div>
          <router-link
            to="/upload"
            class="bg-blue-900 text-white px-8 py-3 rounded-sm hover:bg-blue-800 transition-all font-semibold uppercase tracking-wide text-sm"
          >
            Cargar Resultados
          </router-link>
        </div>
      </div>
    </div>

    <!-- Quota Info -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-blue-800">
            Cuota Mensual: {{ authStore.quotaInfo.remaining }}/{{ authStore.quotaInfo.total }}
          </h3>
          <div class="mt-2 text-sm text-blue-700">
            <div class="w-full bg-blue-200 rounded-full h-2">
              <div 
                class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${(authStore.quotaInfo.remaining / authStore.quotaInfo.total) * 100}%` }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reports List -->
    <div class="bg-white shadow rounded-lg">
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h3 class="text-lg font-semibold text-gray-900 mb-4 sm:mb-0">Historial de Reportes</h3>
          
          <!-- Search and Filter Controls -->
          <div class="flex flex-col sm:flex-row gap-4">
            <!-- Patient Name Search -->
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                v-model="searchQuery"
                @input="filterReports"
                type="text"
                placeholder="Buscar por nombre de paciente..."
                class="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            
            <!-- Date Filter -->
            <div class="flex gap-2">
              <input
                v-model="dateFrom"
                @change="filterReports"
                type="date"
                class="block px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Desde"
              />
              <input
                v-model="dateTo"
                @change="filterReports"
                type="date"
                class="block px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Hasta"
              />
              <button
                @click="clearFilters"
                class="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="loading" class="p-6 text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">Cargando reportes...</p>
      </div>

      <div v-else-if="reports.length === 0" class="p-6 text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No hay reportes</h3>
        <p class="mt-1 text-sm text-gray-500">Comienza subiendo tu primer análisis de laboratorio.</p>
        <div class="mt-6">
          <router-link
            to="/upload"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Subir Primer Reporte
          </router-link>
        </div>
      </div>

      <div v-else-if="filteredReports.length === 0" class="p-6 text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <h3 class="mt-2 text-sm font-medium text-gray-900">No se encontraron reportes</h3>
        <p class="mt-1 text-sm text-gray-500">Intenta ajustar los filtros de búsqueda.</p>
        <div class="mt-6">
          <button
            @click="clearFilters"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paciente</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" v-if="authStore.isAdmin">Usuario</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edad</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sexo</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="report in filteredReports" :key="report.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-8 w-8">
                    <div class="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <svg class="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3">
                    <div class="text-sm font-medium text-gray-900">
                      {{ report.patientName || 'Paciente no especificado' }}
                    </div>
                  </div>
                </div>
              </td>
              <td v-if="authStore.isAdmin" class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ report.user?.email || report.user?.name || (report.owner_user_id ? report.owner_user_id.substring(0,8) + '…' : 'N/A') }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ formatDate(report.createdAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ report.patientAge ? `${report.patientAge} años` : '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ report.patientSex || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="[
                  'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                  report.status === 'completed' 
                    ? 'bg-green-100 text-green-800' 
                    : report.status === 'processing'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                ]">
                  {{ getStatusText(report.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <router-link
                  :to="`/results_v2/${report.id}`"
                  class="text-blue-600 hover:text-blue-900"
                >
                  Ver Resultados
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.pages > 1 && !(searchQuery || dateFrom || dateTo)" class="px-6 py-4 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (pagination.page - 1) * pagination.limit + 1 }} a 
            {{ Math.min(pagination.page * pagination.limit, pagination.total) }} de 
            {{ pagination.total }} resultados
          </div>
          <div class="flex items-center space-x-2">
            <!-- Previous Button -->
            <button
              @click="loadReports(pagination.page - 1)"
              :disabled="pagination.page <= 1"
              class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            <!-- Page Numbers -->
            <div class="flex space-x-1">
              <!-- First page -->
              <button
                v-if="pagination.page > 3"
                @click="loadReports(1)"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                1
              </button>
              <span v-if="pagination.page > 4" class="px-3 py-2 text-gray-500">...</span>
              
              <!-- Pages around current page -->
              <button
                v-for="page in getVisiblePages()"
                :key="page"
                @click="loadReports(page)"
                :class="[
                  'px-3 py-2 border rounded-md text-sm font-medium',
                  page === pagination.page
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                ]"
              >
                {{ page }}
              </button>
              
              <!-- Last page -->
              <span v-if="pagination.page < pagination.pages - 3" class="px-3 py-2 text-gray-500">...</span>
              <button
                v-if="pagination.page < pagination.pages - 2"
                @click="loadReports(pagination.pages)"
                class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {{ pagination.pages }}
              </button>
            </div>
            
            <!-- Next Button -->
            <button
              @click="loadReports(pagination.page + 1)"
              :disabled="pagination.page >= pagination.pages"
              class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useReportStore } from '../stores/reportStore';

const authStore = useAuthStore();
const reportStore = useReportStore();

// State
const reports = ref<any[]>([]);
const loading = ref<boolean>(false);
const pagination = ref<{ page: number; limit: number; total: number; pages: number}>({
  page: 1,
  limit: 10,
  total: 0,
  pages: 0
});

// Filter state
const searchQuery = ref<string>('');
const dateFrom = ref<string>('');
const dateTo = ref<string>('');
const filteredReports = ref<any[]>([]);

// Methods
const loadReports = async (page = 1) => {
  loading.value = true;
  try {
    // Si hay filtros activos, cargar todos los reportes para filtrar
    const limit = (searchQuery.value || dateFrom.value || dateTo.value) ? 1000 : 10;
    const result = await reportStore.listReports(page, limit);
    reports.value = result.reports;
    filteredReports.value = [...result.reports]; // Initialize filtered reports
    
    // Usar información de paginación del backend
    pagination.value = {
      page: result.pagination.page,
      limit: result.pagination.limit,
      total: result.pagination.total,
      pages: result.pagination.pages
    };
  } catch (error) {
    console.error('Error loading reports:', error);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatusText = (status: string) => {
  const statusMap = {
    'completed': 'Completado',
    'processing': 'Procesando',
    'failed': 'Error'
  };
  return statusMap[status] || status;
};

// Filter methods
const filterReports = async () => {
  // Si hay filtros activos, recargar todos los datos
  if (searchQuery.value.trim() || dateFrom.value || dateTo.value) {
    loading.value = true;
    try {
      const result = await reportStore.listReports(1, 1000); // Cargar todos los reportes
      reports.value = result.reports;
    } catch (error) {
      console.error('Error loading all reports for filtering:', error);
    } finally {
      loading.value = false;
    }
  }
  
  let filtered = [...reports.value];
  
  // Filter by patient name
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(report => 
      report.patientName?.toLowerCase().includes(query)
    );
  }
  
  // Filter by date range
  if (dateFrom.value) {
    const fromDate = new Date(dateFrom.value);
    const fromDateStr = fromDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    filtered = filtered.filter(report => {
      const reportDate = new Date(report.createdAt);
      const reportDateStr = reportDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      return reportDateStr >= fromDateStr;
    });
  }
  
  if (dateTo.value) {
    const toDate = new Date(dateTo.value);
    const toDateStr = toDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    filtered = filtered.filter(report => {
      const reportDate = new Date(report.createdAt);
      const reportDateStr = reportDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      return reportDateStr <= toDateStr;
    });
  }
  
  filteredReports.value = filtered;
};

const clearFilters = async () => {
  searchQuery.value = '';
  dateFrom.value = '';
  dateTo.value = '';
  // Recargar datos paginados cuando se limpian los filtros
  await loadReports(1);
};

// Pagination helper
const getVisiblePages = () => {
  const current = pagination.value.page;
  const total = pagination.value.pages;
  const pages = [];
  
  // Show 2 pages before and after current page
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
};

// Lifecycle
onMounted(() => {
  loadReports();
});
</script>
